import { useCallback } from "react";

import {
  ErrorResponse,
  getErrorMessages,
  useGetInvestmentHistoryQuery,
} from "../../../../services";
import { UNVERSIONED_GENERATION } from "./useLoadGeneration";
import { useRetainedSection } from "./useRetainedSection";
import { UseInvestmentHistoryProps, UseInvestmentHistoryReturn } from "./types";

/**
 * Thin wrapper over the generated `useGetInvestmentHistoryQuery` — RTK Query
 * fetches on mount and refetches automatically when `id`/`window` change or
 * the `InvestmentHistory` tag is invalidated.
 */
export const useInvestmentHistory = ({
  id,
  window,
  generation = UNVERSIONED_GENERATION,
}: UseInvestmentHistoryProps): UseInvestmentHistoryReturn => {
  const query = useGetInvestmentHistoryQuery(
    {
      id,
      window,
      context: generation.context,
      generation: generation.generation,
    },
    { skip: !generation.isReady },
  );
  // Scoped on id and window, never the pair, so another route or window can't show this one's points.
  const {
    response,
    error: sectionError,
    refreshFailed,
    isRefreshing,
  } = useRetainedSection({
    query,
    section: "history",
    scopeKey: `${id}:${window}`,
    generation,
  });

  const error = sectionError
    ? (getErrorMessages(sectionError as ErrorResponse)[0] ?? null)
    : null;

  const refetch = query.refetch;
  const reload = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    points: response?.result.points ?? [],
    range: response?.result.range,
    freshness: response?.freshness ?? null,
    isLoading: response === undefined && error === null,
    error,
    refreshFailed,
    isRefreshing,
    reload,
  };
};
